import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  TableContainer, Button, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Switch, FormControlLabel, MenuItem, Select,
  InputLabel, FormControl, Chip, CircularProgress, Tooltip, Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { adminApi } from '../../api';

const MODALITIES = ['', 'CT', 'MR', 'CR', 'DX', 'US', 'NM', 'PT', 'PET', 'XA', 'MG', 'RF', 'SC'];
const EMPTY_RULE = {
  rule_name: '', facility_id: null, modality: '', use_last_accessed: false,
  days_to_cool: '', days_to_archive: '', days_to_delete: '',
  days_warning_before_delete: 30, priority: 100, enabled: true,
};

const DayField = ({ label, value, onChange }) => (
  <TextField
    label={label} size="small" type="number"
    value={value ?? ''} onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))}
    placeholder="Never"
    InputLabelProps={{ sx: { color: '#666' } }}
    inputProps={{ min: 1 }}
    sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 0 }, '& input': { color: '#e0e0e0' } }}
  />
);

const LcmRules = () => {
  const [rules, setRules] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => Promise.all([adminApi.getLcmRules(), adminApi.getFacilities()])
    .then(([r, f]) => { setRules(r); setFacilities(f.filter(x => x.name !== '__DEFAULT__')); })
    .catch(console.error).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const openCreate = () => setDialog({ mode: 'create', data: { ...EMPTY_RULE } });
  const openEdit = (r) => setDialog({ mode: 'edit', data: { ...r, modality: r.modality || '' } });
  const close = () => { setDialog(null); setPreview(null); };

  const save = async () => {
    setSaving(true);
    try {
      const d = { ...dialog.data, modality: dialog.data.modality || null, facility_id: dialog.data.facility_id || null };
      if (dialog.mode === 'create') await adminApi.createLcmRule(d);
      else await adminApi.updateLcmRule(d.id, d);
      close(); load();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    await adminApi.deleteLcmRule(id);
    load();
  };

  const runPreview = async (id) => {
    setPreview('loading');
    try {
      const result = await adminApi.previewLcmRule(id);
      setPreview(result);
    } catch (e) { setPreview(null); }
  };

  const upd = (key, val) => setDialog(d => ({ ...d, data: { ...d.data, [key]: val } }));

  const scopeLabel = (r) => {
    const parts = [];
    if (r.facility_name) parts.push(r.facility_name);
    if (r.modality) parts.push(r.modality);
    return parts.length ? parts.join(' · ') : 'Global';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography sx={{ color: 'primary.main', fontSize: 20, fontWeight: 700, letterSpacing: 1 }}>LCM Rules</Typography>
        <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={openCreate}
          sx={{ borderRadius: 0, borderColor: '#333', color: '#aaa', '&:hover': { borderColor: 'primary.main', color: 'primary.main' } }}>
          Add Rule
        </Button>
      </Box>

      <Typography sx={{ color: '#555', fontSize: 12, mb: 2 }}>
        Rules are evaluated by priority (lowest = first). Most specific match wins: facility+modality &gt; facility &gt; modality &gt; global. Global rules only apply to facilities with LCM enabled.
      </Typography>

      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper} sx={{ bgcolor: '#111', border: '1px solid #1e1e1e', borderRadius: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Pri', 'Rule Name', 'Scope', 'Trigger', 'Cool', 'Archive', 'Delete', 'Status', ''].map(h => (
                  <TableCell key={h} sx={{ bgcolor: '#0d0d0d', color: 'primary.main', fontSize: 11, fontWeight: 700, letterSpacing: 0.8, borderBottom: '1px solid #222' }}>
                    {h.toUpperCase()}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map(r => (
                <TableRow key={r.id} sx={{ '&:hover': { bgcolor: '#141414' } }}>
                  <TableCell sx={{ color: '#aaa', fontSize: 12, fontFamily: 'monospace', borderBottom: '1px solid #1a1a1a' }}>{r.priority}</TableCell>
                  <TableCell sx={{ color: '#e0e0e0', fontSize: 13, borderBottom: '1px solid #1a1a1a' }}>{r.rule_name}</TableCell>
                  <TableCell sx={{ borderBottom: '1px solid #1a1a1a' }}>
                    <Chip label={scopeLabel(r)} size="small" sx={{ fontSize: 10, height: 18, bgcolor: '#1a1a2e', color: '#7986cb', borderRadius: 0 }} />
                  </TableCell>
                  <TableCell sx={{ color: '#666', fontSize: 11, borderBottom: '1px solid #1a1a1a' }}>
                    {r.use_last_accessed ? 'Last accessed' : 'Received date'}
                  </TableCell>
                  <TableCell sx={{ color: '#7986cb', fontSize: 12, fontFamily: 'monospace', borderBottom: '1px solid #1a1a1a' }}>
                    {r.days_to_cool ? `${r.days_to_cool}d` : '—'}
                  </TableCell>
                  <TableCell sx={{ color: '#ff8a65', fontSize: 12, fontFamily: 'monospace', borderBottom: '1px solid #1a1a1a' }}>
                    {r.days_to_archive ? `${r.days_to_archive}d` : '—'}
                  </TableCell>
                  <TableCell sx={{ color: '#ef9a9a', fontSize: 12, fontFamily: 'monospace', borderBottom: '1px solid #1a1a1a' }}>
                    {r.days_to_delete ? `${r.days_to_delete}d` : '—'}
                  </TableCell>
                  <TableCell sx={{ borderBottom: '1px solid #1a1a1a' }}>
                    <Chip label={r.enabled ? 'ON' : 'OFF'} size="small"
                      sx={{ fontSize: 10, height: 18, bgcolor: r.enabled ? '#003322' : '#1a1a1a', color: r.enabled ? '#66bb6a' : '#555', borderRadius: 0 }} />
                  </TableCell>
                  <TableCell sx={{ borderBottom: '1px solid #1a1a1a' }}>
                    <Tooltip title="Preview impact"><IconButton size="small" onClick={() => runPreview(r.id)} sx={{ color: '#555', '&:hover': { color: '#66bb6a' } }}><PlayArrowIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(r)} sx={{ color: '#555', '&:hover': { color: 'primary.main' } }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" onClick={() => del(r.id)} sx={{ color: '#555', '&:hover': { color: '#ef9a9a' } }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Preview result */}
      {preview && preview !== 'loading' && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ bgcolor: '#0d1a2e', color: '#aaa', fontSize: 12 }}>
            <Typography sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>Rule Preview — {preview.rule?.rule_name}</Typography>
            {Object.entries(preview.would_affect || {}).map(([action, data]) => (
              <Box key={action} sx={{ mb: 0.5 }}>
                <strong style={{ color: '#e0e0e0' }}>{action.replace('_', ' ').toUpperCase()}:</strong>{' '}
                {data.study_count} studies ({data.instance_count?.toLocaleString() ?? 0} instances) after {data.after_days} days
              </Box>
            ))}
          </Alert>
        </Box>
      )}

      {/* Create/Edit dialog */}
      <Dialog open={!!dialog} onClose={close} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: '#111', border: '1px solid #222', borderRadius: 1 } }}>
        <DialogTitle sx={{ color: 'primary.main', fontSize: 14, letterSpacing: 1 }}>
          {dialog?.mode === 'create' ? 'ADD LCM RULE' : 'EDIT LCM RULE'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Rule Name" size="small" fullWidth value={dialog?.data?.rule_name ?? ''}
              onChange={e => upd('rule_name', e.target.value)}
              InputLabelProps={{ sx: { color: '#666' } }} inputProps={{ style: { color: '#e0e0e0' } }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }} />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel sx={{ color: '#666' }}>Facility</InputLabel>
                <Select value={dialog?.data?.facility_id ?? ''} label="Facility"
                  onChange={e => upd('facility_id', e.target.value || null)}
                  sx={{ borderRadius: 0, color: '#e0e0e0' }}>
                  <MenuItem value="">All Facilities</MenuItem>
                  {facilities.map(f => <MenuItem key={f.id} value={f.id}>{f.display_name || f.name}{!f.lcm_enabled ? ' — LCM OFF' : ''}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel sx={{ color: '#666' }}>Modality</InputLabel>
                <Select value={dialog?.data?.modality ?? ''} label="Modality"
                  onChange={e => upd('modality', e.target.value)}
                  sx={{ borderRadius: 0, color: '#e0e0e0' }}>
                  {MODALITIES.map(m => <MenuItem key={m} value={m}>{m || 'All Modalities'}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>

            {dialog?.data?.facility_id && facilities.find(f => f.id === dialog.data.facility_id && !f.lcm_enabled) && (
              <Alert severity="warning" sx={{ bgcolor: '#2e230d', color: '#ddd', fontSize: 12 }}>
                This facility has LCM disabled. The rule can be saved, but preview/execution will affect zero studies until facility LCM is explicitly enabled.
              </Alert>
            )}

            <Typography sx={{ color: '#555', fontSize: 11 }}>LIFECYCLE THRESHOLDS (days)</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <DayField label="Days → Cool" value={dialog?.data?.days_to_cool} onChange={v => upd('days_to_cool', v)} />
              <DayField label="Days → Archive" value={dialog?.data?.days_to_archive} onChange={v => upd('days_to_archive', v)} />
              <DayField label="Days → Delete" value={dialog?.data?.days_to_delete} onChange={v => upd('days_to_delete', v)} />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Warning Days Before Delete" size="small" type="number"
                value={dialog?.data?.days_warning_before_delete ?? 30}
                onChange={e => upd('days_warning_before_delete', Number(e.target.value))}
                InputLabelProps={{ sx: { color: '#666' } }} inputProps={{ style: { color: '#e0e0e0' }, min: 0 }}
                sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 0 } }} />
              <TextField label="Priority" size="small" type="number"
                value={dialog?.data?.priority ?? 100}
                onChange={e => upd('priority', Number(e.target.value))}
                InputLabelProps={{ sx: { color: '#666' } }} inputProps={{ style: { color: '#e0e0e0' }, min: 1 }}
                sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 0 } }} />
            </Box>

            <FormControlLabel
              control={<Switch checked={dialog?.data?.use_last_accessed ?? false} onChange={e => upd('use_last_accessed', e.target.checked)} size="small" />}
              label={<Typography sx={{ color: '#aaa', fontSize: 13 }}>Trigger based on last accessed (not received date)</Typography>}
            />
            <FormControlLabel
              control={<Switch checked={dialog?.data?.enabled ?? true} onChange={e => upd('enabled', e.target.checked)} size="small" />}
              label={<Typography sx={{ color: '#aaa', fontSize: 13 }}>Rule Enabled</Typography>}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={close} sx={{ color: '#555' }}>Cancel</Button>
          <Button onClick={save} disabled={saving} variant="outlined" sx={{ borderRadius: 0, borderColor: 'primary.main', color: 'primary.main' }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LcmRules;
